// src/components/card/CardFolderStatus.tsx

import { NewReleasesTwoTone } from '@mui/icons-material'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  FolderConsumptionAvailability,
  FolderId,
  FolderImage,
  FolderName,
  FolderShortName,
  KindOfUse,
} from '@/types/domain'

import { HStack } from '..'
import { getKindOfUseIcon, SVGStatus } from '../../api'
import {
  FolderStatus,
  useFolderImage,
  useFolderKindOfUse,
  useFolderName,
  useFolderShortName,
  useFolderStatus,
  useFolderTCA,
} from '../../hooks'

const descriptions = {
  green: 'Alles OK',
  yellow: 'Mindestens ein Sensor befindet sich nahe dem kritischen Bereich',
  red: 'Mindestens ein Sensor befindet sich im kritischen Bereich',
  neutral: 'Kein Sensor registriert',
}

type FolderBigPicProps = {
  id: FolderId
}

const FolderBigPic = ({ id }: FolderBigPicProps) => {
  const navigate = useNavigate()
  const fImage: FolderImage = useFolderImage(id)

  const gotoFolder = useCallback(() => {
    const url = `/folder/${id}/home`

    navigate(url)
  }, [navigate, id])

  return (
    <Box
      component={'img'}
      draggable={false}
      onClick={gotoFolder}
      src={fImage}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        height: 1,
        width: 1,
        minWidth: 1,
        objectFit: 'cover',
        cursor: 'pointer',
        '&:hover': { transform: 'scale(1.1)' },
      }}
    />
  )
}

type FolderUsagesProps = {
  id: FolderId
}

const FolderUsages = ({ id }: FolderUsagesProps) => {
  const data: KindOfUse = useFolderKindOfUse(id)

  return (
    <HStack sx={{ position: 'absolute', left: 10, bottom: 10 }}>
      {data?.split(/[&+,;]/).map(item => {
        const trimmed = item.trim()
        const icon = getKindOfUseIcon(trimmed)

        return (
          <Chip
            key={`trimmed-${trimmed}`}
            icon={icon}
            label={trimmed}
            sx={{ bgcolor: '#DDD' }}
          />
        )
      })}
    </HStack>
  )
}

type FolderColorStatusProps = {
  id: FolderId
}

const FolderColorStatus = ({ id }: FolderColorStatusProps) => {
  const fStatus: FolderStatus = useFolderStatus(id)
  const navigate = useNavigate()

  const gotoFolderCategory = useCallback(
    category => {
      const url = `/folder/${id}/home/${category}`

      navigate(url)
    },
    [navigate, id]
  )

  return (
    <HStack>
      {Object.keys(fStatus)
        .filter(f => f !== 'canCorrelate')
        .map(item => {
          const status = fStatus[item]
          const title = descriptions[status]
          const onClick = () => gotoFolderCategory(item)

          return (
            <Tooltip key={item} placement={'top'} title={title}>
              <Box onClick={onClick} sx={{ cursor: 'pointer', width: 50 }}>
                <SVGStatus type={item} status={status} />
              </Box>
            </Tooltip>
          )
        })}
    </HStack>
  )
}

type CardFolderStatusProps = {
  id: FolderId
}

export const CardFolderStatus = ({ id }: CardFolderStatusProps) => {
  const fName: FolderName = useFolderName(id)
  const fShort: FolderShortName = useFolderShortName(id)
  const fCa: FolderConsumptionAvailability = useFolderTCA(id)

  return (
    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 5 }}>
      <CardHeader
        action={
          !fCa ? (
            <Tooltip title={'Kein Gesamtverbrauch vorhanden'}>
              <IconButton>
                <NewReleasesTwoTone />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
        subheader={fShort}
        title={fName}
      />

      <CardContent
        sx={{ height: 360, overflow: 'hidden', p: 0, position: 'relative' }}
      >
        <FolderBigPic id={id} />
        <FolderUsages id={id} />
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Box />
        <FolderColorStatus id={id} />
      </CardActions>
    </Card>
  )
}
